export interface MontageSelectionAsset {
  DomainId: string;
  Detail: {
    ClientKey: string;
    WebUrl: string;
    FileName: string;
  };
}

export interface MontageSelectionProduct {
  Id: string;
  Detail: {
    ClientKey: string;
  };
  FacingAsset: {
    WebUrl: string;
    FileName: string;
    AssetId: string;
  };
}

/** Offer-selected asset domain shape or flat asset API response. */
export function normalizeSelectionAsset(item: any): MontageSelectionAsset {
  if (item?.Detail?.WebUrl) {
    return {
      DomainId: item.DomainId ?? item.Id ?? '',
      Detail: {
        ClientKey: item.Detail.ClientKey ?? '',
        WebUrl: item.Detail.WebUrl,
        FileName: item.Detail.FileName ?? item.Detail.Name ?? '',
      },
    };
  }

  return {
    DomainId: item?.Id ?? item?.DomainId ?? '',
    Detail: {
      ClientKey: item?.ClientKey ?? '',
      WebUrl: item?.WebUrl ?? '',
      FileName: item?.FileName ?? item?.AssetName ?? '',
    },
  };
}

/** Offer-selected product domain shape or product API response. */
export function normalizeSelectionProduct(item: any): MontageSelectionProduct {
  const facingAsset = item?.FacingAsset;
  const clientKey =
    item?.Detail?.ClientKey ??
    item?.ClientKey ??
    facingAsset?.FileName ??
    facingAsset?.AssetName ??
    item?.Detail?.UPC ??
    '';

  return {
    Id: item?.Id ?? item?.DomainId ?? '',
    Detail: { ClientKey: clientKey },
    FacingAsset: {
      WebUrl: facingAsset?.WebUrl ?? item?.WebUrl ?? item?.Detail?.WebUrl ?? '',
      FileName: facingAsset?.FileName ?? item?.FileName ?? item?.Detail?.FileName ?? '',
      AssetId: facingAsset?.AssetId ?? '',
    },
  };
}

/** Asset GUID for ChildAssets payload — flat asset Id or product FacingAsset.AssetId. */
export function getChildAssetId(
  item: MontageSelectionAsset | MontageSelectionProduct
): string {
  if ('FacingAsset' in item) {
    return item.FacingAsset?.AssetId ?? '';
  }

  return item.DomainId ?? '';
}

/** Match JAI settings keys when product API omits Detail.ClientKey. */
export function assignJaiClientKeyToProduct(
  item: MontageSelectionProduct,
  rawItem: any,
  clientKeys: string[]
): MontageSelectionProduct {
  if (item.Detail.ClientKey) {
    return item;
  }

  const candidates = [
    rawItem?.Detail?.ClientKey,
    rawItem?.ClientKey,
    rawItem?.FacingAsset?.FileName,
    rawItem?.FacingAsset?.AssetName,
    rawItem?.Detail?.UPC,
  ].filter(Boolean);

  const match = clientKeys.find((key) => candidates.includes(key));
  if (!match) {
    return item;
  }

  return {
    ...item,
    Detail: { ...item.Detail, ClientKey: match },
  };
}

export function getSelectionItemKey(
  item: MontageSelectionAsset | MontageSelectionProduct | any
): string {
  return item?.Detail?.ClientKey || item?.ClientKey || item?.DomainId || item?.Id || '';
}

function parseChildAssetClientKey(configJson: string): string {
  try {
    const parsed = JSON.parse(configJson);
    return (
      parsed?.ClientKey ||
      parsed?.montageImageConfig?.ClientKey ||
      parsed?.montageImageConfig?.refId ||
      parsed?.montageImageDetails?.ClientKey ||
      parsed?.montageImageDetails?.wicId ||
      ''
    );
  } catch {
    return '';
  }
}

export function parseChildAssetClientKeys(childAssets: any[]): string[] {
  return childAssets
    .map((childAsset) => parseChildAssetClientKey(childAsset.Config))
    .filter(Boolean);
}
